


























































































































































































































































































import addWillExecutorDialog from '@/components/client/view-client/add-will-executor-dialog.vue';
import addWillGuardianDialog from '@/components/client/view-client/add-will-guardian-dialog.vue';
import editWillExecutorDialog from '@/components/client/view-client/edit-will-executor-dialog.vue';
import editWillGuardianDialog from '@/components/client/view-client/edit-will-guardian-dialog.vue';
import { ClientAccount } from '@/shared/clients/client-account';
import { ClientAccountWill } from '@/shared/clients/client-account-will';
import { ClientAccountWillExecutor } from '@/shared/clients/client-account-will-executor';
import { ClientAccountWillGuardian } from '@/shared/clients/client-account-will-guardian';
import { WillType } from '@/shared/types/will-type';
import { formatDate } from '@/shared/utilities/date-utilities';
import { Validator } from 'vee-validate';
import { Component, Inject, Prop, Vue } from 'vue-property-decorator';
import { namespace } from 'vuex-class';

const userModule = namespace('user');
const typesModule = namespace('types');

@Component({
    components: {
        addWillExecutorDialog,
        editWillExecutorDialog,
        addWillGuardianDialog,
        editWillGuardianDialog,
    },
})
export default class ClientWillDetails extends Vue {
    @userModule.Getter('isAdministrator') private isAdministrator!: boolean;
    @userModule.Getter('isInternalKeyStakeholder') private isInternalKeyStakeholder!: boolean;
    @userModule.Getter('isCustomerExperienceExecutiveTeamLeader') private isCustomerExperienceExecutiveTeamLeader!: boolean;
    @userModule.Getter('isDataCreationTeamLeader') private isDataCreationTeamLeader!: boolean;
    @userModule.Getter('isDataCreationExecutive') private isDataCreationExecutive!: boolean;
    @typesModule.Getter('activeWillTypes') private willTypes!: Array<WillType>;

    @Inject('$validator') public $validator!: Validator;
    @Prop({ required: true }) private client!: ClientAccount;

    private addGuardianDialogVisible: boolean = false;
    private editGuardianDialogVisible: boolean = false;
    private addExecutorDialogVisible: boolean = false;
    private editExecutorDialogVisible: boolean = false;
    private confirmDeleteVisible: boolean = false;
    private confirmExecutorDeleteVisible: boolean = false;
    private confirmGuardianDeleteVisible: boolean = false;
    private selectedExecutor: ClientAccountWillExecutor | null = null;
    private selectedGuardian: ClientAccountWillGuardian | null = null;

    private get will(): ClientAccountWill {
        return this.client!.will!;
    }

    private get willDate(): string | Date | null {
        return formatDate(this.client!.will!.date, true);
    }

    private set willDate(value: string | Date | null) {
        this.client!.will!.date = value;
    }

    private get canDelete(): boolean {
        return this.isAdministrator || this.isCustomerExperienceExecutiveTeamLeader || this.isDataCreationTeamLeader;
    }

    private get canAmendWill(): boolean {
        return (this.isAdministrator || this.isInternalKeyStakeholder || this.isDataCreationTeamLeader);
    }

    private get canAddGuardian(): boolean {
        return (this.isAdministrator || this.isInternalKeyStakeholder || this.isDataCreationTeamLeader || this.isDataCreationExecutive);
    }

    private get canDeleteGuardian(): boolean {
        return (this.isAdministrator || this.isDataCreationTeamLeader);
    }

    private get canAddExecutor(): boolean {
        return (this.isAdministrator || this.isInternalKeyStakeholder || this.isDataCreationTeamLeader || this.isDataCreationExecutive);
    }

    private get canDeleteExecutor(): boolean {
        return (this.isAdministrator || this.isDataCreationTeamLeader);
    }

    private get orderedWillExecutors(): Array<ClientAccountWillExecutor> {
        return [...this.will.executors].sort((n1, n2) => {
            if (n1.displayOrder! > n2.displayOrder!) { return 1; }
            if (n1.displayOrder! < n2.displayOrder!) { return -1; }
            return 0;
        });
    }

    private showConfirmDelete(): void {
        this.confirmDeleteVisible = true;
    }

    private onWillRemoved(): void {
        this.$emit('will-removed', { ...this.will });
    }

    private showAddGuardianDialog(): void {
        this.addGuardianDialogVisible = true;
    }

    private showEditGuardianDialog(guardian: ClientAccountWillGuardian): void {
        if (guardian.id == null) {
            guardian.id = 'editing';
        }

        this.selectedGuardian = { ...guardian };
        this.editGuardianDialogVisible = true;
    }

    private showConfirmGuardianDelete(guardian: ClientAccountWillGuardian): void {
        if (guardian.id == null) {
            guardian.id = 'editing';
        }

        this.selectedGuardian = { ...guardian };
        this.confirmGuardianDeleteVisible = true;
    }

    private onGuardianAdded(guardian: ClientAccountWillGuardian): void {
        this.will.guardians.push(guardian);
        this.onCancel();
    }

    private onGuardianUpdated(guardian: ClientAccountWillGuardian): void {
        const index = this.will.guardians.findIndex((e) => e.id === guardian.id);

        if (index < 0) {
            this.$snackbar.open({ message: 'Could not update guardian.', type: 'is-danger' });
            this.onCancel();
            return;
        }

        if (guardian.id === 'editing') {
            guardian.id = null;
        }

        this.will.guardians.splice(index, 1, { ...guardian });

        this.onCancel();
    }

    private onGuardianRemoved(guardian: ClientAccountWillGuardian): void {
        const index = this.will.guardians.findIndex((e) => e.id === guardian.id);

        if (index < 0) {
            this.$snackbar.open({ message: 'Could not remove guardian.', type: 'is-danger' });
            this.onCancel();
            return;
        }

        if (guardian.id === 'editing') {
            guardian.id = null;
        }

        this.will.guardians.splice(index, 1);

        this.onCancel();
    }

    private showAddExecutorDialog(): void {
        this.addExecutorDialogVisible = true;
    }

    private showEditExecutorDialog(executor: ClientAccountWillExecutor): void {
        if (executor.id == null) {
            executor.id = 'editing';
        }

        this.selectedExecutor = { ...executor };
        this.editExecutorDialogVisible = true;
    }

    private showConfirmExecutorDelete(executor: ClientAccountWillExecutor): void {
        if (executor.id == null) {
            executor.id = 'editing';
        }

        this.selectedExecutor = { ...executor };
        this.confirmExecutorDeleteVisible = true;
    }

    private onExecutorAdded(executor: ClientAccountWillExecutor): void {
        const displayOrder = !this.will.executors.length ? 0 : Math.max(...this.will.executors.map((e) => e.displayOrder!)) + 1;
        this.will.executors.push({ ...executor, displayOrder });
        this.onCancel();
    }

    private onExecutorUpdated(executor: ClientAccountWillExecutor): void {
        const index = this.will.executors.findIndex((e) => e.id === executor.id);

        if (index < 0) {
            this.$snackbar.open({ message: 'Could not update executor.', type: 'is-danger' });
            this.onCancel();
            return;
        }

        if (executor.id === 'editing') {
            executor.id = null;
        }

        this.will.executors.splice(index, 1, { ...executor });
        this.onCancel();
    }

    private onExecutorRemoved(executor: ClientAccountWillExecutor): void {
        const index = this.will.executors.findIndex((e) => e.id === executor.id);

        if (index < 0) {
            this.$snackbar.open({ message: 'Could not remove executor.', type: 'is-danger' });
            this.onCancel();
            return;
        }

        if (executor.id === 'editing') {
            executor.id = null;
        }

        this.will.executors.splice(index, 1);

        this.onCancel();
    }

    private onCancel(): void {
        for (const guardian of this.will.guardians.filter((g) => g.id === 'editing')) {
            guardian.id = null;
        }

        for (const executor of this.will.executors.filter((e) => e.id === 'editing')) {
            executor.id = null;
        }

        this.confirmDeleteVisible = false;

        this.addGuardianDialogVisible = false;
        this.editGuardianDialogVisible = false;
        this.confirmGuardianDeleteVisible = false;
        this.selectedGuardian = null;

        this.addExecutorDialogVisible = false;
        this.editExecutorDialogVisible = false;
        this.confirmExecutorDeleteVisible = false;
        this.selectedExecutor = null;
    }

    private willTypeDescription(willTypeId: string): string {
        const willType = this.willTypes.find((type) => type.id === willTypeId);
        return willType == null ? '' : willType.value;
    }

    private firstOrSubstituteExecutorTypeDescription(executor: ClientAccountWillExecutor): string | null {
        if (executor == null || executor.isFirstOrSubstituteExecutor == null) {
            return null;
        }

        return executor.isFirstOrSubstituteExecutor ? 'Sub Exec' : 'First Exec';
    }

    private soleOrCoExecutorTypeDescription(executor: ClientAccountWillExecutor): string | null {
        if (executor == null || executor.isSoleOrCoExecutor == null) {
            return null;
        }

        return executor.isSoleOrCoExecutor ? 'Co-Exec' : 'Sole Exec';
    }

}
